/**
 * adapty 域 Zod 共享契约 — admin 按 App 管理 Adapty Secret Key + catalog 同步
 *
 * 形状事实源：design/Novel-admin dialogs2.jsx AdaptyConfigDialog + docs/plans/2026-06-25-adapty-integration.md。
 * - 凭证：admin 在「集成」页按 App 录 Adapty Secret Key（加密存 adapty_app_credentials，明文永不回显，只回 key_tail 掩码）。
 * - catalog：placements 来自 Adapty Export placements（developer_id = placement_id）；products（vendor_product_id 候选）
 *   来自 C 端观测表去重（subscription_entitlements / purchase_amounts / operational_popup_scenes），**非 Adapty**
 *   （已三重实测确认 Adapty 公开 API 无 vendor_product_id 端点，见 plan §2）。
 * admin-own 契约，dev-docs 收录义务 #229（同步提 dev-docs PR）。
 */

import { z } from 'zod';
import type { SettlementChannel } from './payment-methods';

/** Adapty 凭证连接状态闭集：connected 已验证 / unverified 已存未验 / invalid 验证失败（Key 被拒）*/
export const ADAPTY_CREDENTIAL_STATUSES = ['connected', 'unverified', 'invalid'] as const;
export type AdaptyCredentialStatus = (typeof ADAPTY_CREDENTIAL_STATUSES)[number];

/**
 * PUT /integrations/adapty/credentials/:appId 入参：录 / 换 Adapty Secret Key。
 * 明文 Key 仅提交、永不回显（响应只回 key_tail 掩码）；格式须以 `secret_live_` 开头（Adapty server-side key）。
 */
export const UpsertAdaptyCredentialRequestSchema = z.object({
  secret_key: z
    .string()
    .trim()
    .min(12)
    .regex(/^secret_live_\S+$/, 'Adapty Secret Key 须以 secret_live_ 开头'),
});
export type UpsertAdaptyCredentialRequest = z.infer<typeof UpsertAdaptyCredentialRequestSchema>;

/** 单条已配置 App 凭证（列表 / upsert / test 返回；明文 Key 永不出现，只回 key_tail 掩码）*/
export type AdaptyCredentialResponse = {
  app_id: string;
  /** App 名（JOIN apps 便于前端展示；apps 软删 / 缺失时为 null）*/
  app_name: string | null;
  /** Secret Key 后 5 位掩码（adaptyMask 用）*/
  key_tail: string;
  status: AdaptyCredentialStatus;
  last_verified_at: string | null;
  last_sync_at: string | null;
  last_sync_placements: number;
  last_sync_products: number;
  updated_at: string;
};

/** 测试连接结果（POST /integrations/adapty/credentials/:appId/test）*/
export type AdaptyCredentialTestResponse = {
  app_id: string;
  status: AdaptyCredentialStatus;
  last_verified_at: string | null;
};

/**
 * placement 下的单个产品（来自 Adapty paywall 的 products[]）。
 * product_id = Adapty 内部 UUID；product_set 如「monthly」。无 store 级 vendor_product_id
 * （Adapty 公开 API catalog 端点不提供，见 docs/plans/2026-06-25-adapty-integration.md §2）。
 */
export type AdaptyPlacementProduct = {
  product_id: string;
  title: string;
  product_set: string | null;
};

/** catalog 单条 placement（来自 Adapty Export placements，developer_id = placement_id）*/
export type AdaptyPlacementItem = {
  placement_id: string;
  placement_name: string;
  paywall_name: string | null;
  /** 唯一且完整的 paywall 集合已核实；false 时空数组不表示无商品。 */
  is_product_scope_verified: boolean;
  /**
   * 该 placement 的 paywall 下的产品（List Paywalls 派生，按 paywall_name ↔ paywall.title 匹配）。
   * 无匹配 paywall / paywall 无产品 → 空数组。
   */
  products: AdaptyPlacementProduct[];
};

/**
 * catalog 单条产品候选（vendor_product_id 来自 C 端观测表）。
 * per-通道（#306）：同 product 在 IAP / STRIPE / 框架通道各一行 product_metadata，`configured_methods` 列出已配置业务效果的通道；
 * `configured` = 至少一个通道已配（= configured_methods 非空），保留供只看「是否已录」的消费方。UI 据 configured_methods
 * 判「某通道待录」（半配产品仍可为缺的那个通道从候选下拉选择）。
 */
export type AdaptyProductCandidate = {
  vendor_product_id: string;
  configured: boolean;
  /** 已配置业务效果的通道（结算通道层 `SETTLEMENT_CHANNELS`）*/
  configured_methods: SettlementChannel[];
};

/** GET /payments/adapty/catalog?app_id=&refresh= 返回（未配置 → PAY_ADAPTY_NOT_CONFIGURED 422；上游故障 → 503）*/
export type AdaptyCatalogResponse = {
  app_id: string;
  placements: AdaptyPlacementItem[];
  products: AdaptyProductCandidate[];
  /** 本次 placement 同步时刻（ISO 8601）*/
  synced_at: string;
};
